// frontend/src/utils/duration.js
//
// Formato ÚNICO de duraciones para toda la aplicación.
//
// Regla: a partir de 24 h se habla en días, nunca en horas acumuladas. Un ticket
// abierto hace 12 días decía "427h 51m", que nadie lee de un vistazo.
//
//   < 1 h    ->  "45m"
//   < 24 h   ->  "7h 20m"
//   >= 24 h  ->  "12d 14h"

const asMinutes = (value, unit) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  switch (unit) {
    case 'seconds': return n / 60;
    case 'hours': return n * 60;
    case 'ms': return n / 60000;
    default: return n;         // 'minutes'
  }
};

/**
 * Duración legible.
 * @param {number} value cantidad
 * @param {'minutes'|'seconds'|'hours'|'ms'} unit unidad de `value`
 */
export const formatDuration = (value, unit = 'minutes') => {
  const minutes = asMinutes(value, unit);
  if (minutes === null) return '—';

  const totalMinutes = Math.floor(minutes);
  if (totalMinutes < 60) return `${totalMinutes}m`;

  const hours = Math.floor(totalMinutes / 60);
  if (hours < 24) return `${hours}h ${totalMinutes % 60}m`;

  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
};

/** Duración transcurrida desde una fecha ISO hasta ahora. */
export const formatElapsedSince = (isoDate) => {
  if (!isoDate) return '—';
  const start = new Date(isoDate).getTime();
  if (!Number.isFinite(start)) return '—';
  return formatDuration(Math.max(0, Date.now() - start), 'ms');
};

/** "Abierto hace 12d 14h" (o el prefijo que se le pase). */
export const formatOpenFor = (isoDate, prefix = 'Abierto hace') =>
  `${prefix} ${formatElapsedSince(isoDate)}`;

/**
 * Valor para un `<input type="datetime-local">` con la fecha de HOY en la hora
 * local del navegador. Se usa como vencimiento por defecto al crear tareas: la
 * tarea nace para atenderse hoy, no en una fecha vacía.
 */
export const todayDueDateTimeLocal = (hour = 18, minute = 0) => {
  const now = new Date();
  const due = new Date(now);
  due.setHours(hour, minute, 0, 0);
  // Si ya pasó la hora propuesta, se ancla al final del día para no nacer vencida.
  if (due.getTime() <= now.getTime()) due.setHours(23, 59, 0, 0);

  const pad = (n) => String(n).padStart(2, '0');
  return `${due.getFullYear()}-${pad(due.getMonth() + 1)}-${pad(due.getDate())}`
    + `T${pad(due.getHours())}:${pad(due.getMinutes())}`;
};

export default { formatDuration, formatElapsedSince, formatOpenFor, todayDueDateTimeLocal };
